/**
 * T+ Signal History Panel — track past signals and their outcomes
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  alpha, ToggleButton, ToggleButtonGroup, CircularProgress, Chip,
} from '@mui/material';
import {
  History, CheckCircle, Cancel, HourglassEmpty, TrendingUp,
  Assessment, EmojiEvents, TrendingDown, Speed, ShowChart,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tplusAPI } from '../../services/api';

const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '–';
const fmtPct = (n, d = 2) => n != null ? `${n >= 0 ? '+' : ''}${(n * 100).toFixed(d)}%` : '–';

const OUTCOME_CONFIG = {
  tp2:     { color: '#10b981', key: 'tplus.history.outcomeTP2' },
  tp1:     { color: '#34d399', key: 'tplus.history.outcomeTP1' },
  win:     { color: '#10b981', key: 'tplus.history.outcomeWin' },
  stop:    { color: '#ef4444', key: 'tplus.history.outcomeStop' },
  loss:    { color: '#ef4444', key: 'tplus.history.outcomeLoss' },
  active:  { color: '#3b82f6', key: 'tplus.history.outcomeActive' },
  pending: { color: '#94a3b8', key: 'tplus.history.outcomePending' },
};

const StatTile = ({ label, value, color, Icon }) => (
  <Box sx={{
    flex: 1, minWidth: 0,
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5,
    border: `1px solid ${alpha(color, 0.12)}`,
    bgcolor: '#fff',
    p: '10px 12px',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: alpha(color, 0.3),
      boxShadow: `0 2px 8px ${alpha(color, 0.08)}`,
    },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1, flexShrink: 0,
        bgcolor: alpha(color, 0.08),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 16, color }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.4px', color: '#94a3b8', lineHeight: 1,
        }}>{label}</Typography>
        <Typography sx={{
          fontSize: '1.25rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.2, fontFamily: 'monospace',
        }}>{value}</Typography>
      </Box>
    </Box>
  </Box>
);

const SignalHistoryPanel = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    tplusAPI.getSignalHistory({ days })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress size={24} sx={{ color: '#94a3b8', mb: 1 }} />
        <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>{t('tplus.history.loading')}</Typography>
      </Box>
    );
  }

  const signals = data?.signals || [];
  const stats = data?.stats || {};

  if (signals.length === 0) {
    return (
      <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <History sx={{ fontSize: 24, color: '#cbd5e1' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8' }}>{t('tplus.history.emptyTitle')}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 420 }}>{t('tplus.history.emptyDesc')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Period selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <History sx={{ fontSize: 18, color: '#667eea' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>{t('tplus.history.title')}</Typography>
        </Box>
        <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={90} sx={{ px: 1.5, py: 0.3, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none' }}>{t('tplus.history.days90')}</ToggleButton>
          <ToggleButton value={180} sx={{ px: 1.5, py: 0.3, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none' }}>{t('tplus.history.days180')}</ToggleButton>
          <ToggleButton value={365} sx={{ px: 1.5, py: 0.3, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none' }}>{t('tplus.history.days365')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2.5 }}>
        <StatTile
          label={t('tplus.history.statsTotal')}
          value={stats.total || 0}
          color="#3b82f6"
          Icon={Assessment}
        />
        <StatTile
          label={t('tplus.history.statsClosed')}
          value={stats.closed || 0}
          color="#6366f1"
          Icon={History}
        />
        <StatTile
          label={t('tplus.history.statsWins')}
          value={stats.wins || 0}
          color="#10b981"
          Icon={EmojiEvents}
        />
        <StatTile
          label={t('tplus.history.statsLosses')}
          value={stats.losses || 0}
          color="#ef4444"
          Icon={TrendingDown}
        />
        <StatTile
          label={t('tplus.history.statsWinRate')}
          value={stats.win_rate != null ? `${stats.win_rate}%` : '–'}
          color={stats.win_rate >= 50 ? '#10b981' : '#ef4444'}
          Icon={Speed}
        />
        <StatTile
          label={t('tplus.history.statsAvgPnl')}
          value={stats.avg_pnl != null ? `${stats.avg_pnl >= 0 ? '+' : ''}${stats.avg_pnl}%` : '–'}
          color={stats.avg_pnl >= 0 ? '#10b981' : '#ef4444'}
          Icon={ShowChart}
        />
      </Box>

      {/* Signals table */}
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
              <TableCell sx={thSx}>{t('tplus.history.headerDate')}</TableCell>
              <TableCell sx={thSx}>Setup</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.entry')}</TableCell>
              <TableCell align="right" sx={thSx}>Stop</TableCell>
              <TableCell align="right" sx={thSx}>TP1</TableCell>
              <TableCell align="right" sx={thSx}>TP2</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.header.confidence')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.history.headerOutcome')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.history.headerPnl')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.history.headerDays')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signals.map((s) => {
              const oc = OUTCOME_CONFIG[s.outcome] || OUTCOME_CONFIG.pending;
              const pnlClr = s.pnl_pct != null ? (s.pnl_pct >= 0 ? '#10b981' : '#ef4444') : '#94a3b8';
              const confClr = s.confidence_score >= 60 ? '#10b981' : s.confidence_score >= 50 ? '#f59e0b' : '#ef4444';
              const setupClr = s.setup_type === 'Spring' ? '#10b981' : s.setup_type === 'NarrowSpring' ? '#059669' : s.setup_type === 'MAConverge' ? '#0ea5e9' : s.setup_type === 'GapAndGo' ? '#8b5cf6' : s.setup_type === 'ConfirmBreak' ? '#f59e0b' : '#3b82f6';
              return (
                <TableRow key={s.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{s.ticker}</Typography>
                    {s.sector && <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{s.sector}</Typography>}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, color: '#64748b', fontFamily: 'monospace', fontSize: '0.78rem' }}>{s.signal_date}</TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: setupClr }}>{t(`tplus.setup.${s.setup_type}`, s.setup_type)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600 }}>{fmt(s.entry_price)}</TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>{fmt(s.stop_price)}</TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{fmt(s.tp1_price)}</TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{fmt(s.tp2_price)}</TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 28, height: 4, bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${s.confidence_score}%`, height: '100%', bgcolor: confClr, borderRadius: 1 }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: confClr }}>{s.confidence_score?.toFixed(0)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <Chip
                      size="small"
                      label={t(oc.key)}
                      icon={
                        s.outcome === 'active' ? <TrendingUp sx={{ fontSize: 13 }} /> :
                        ['tp1', 'tp2', 'win'].includes(s.outcome) ? <CheckCircle sx={{ fontSize: 13 }} /> :
                        ['stop', 'loss'].includes(s.outcome) ? <Cancel sx={{ fontSize: 13 }} /> :
                        <HourglassEmpty sx={{ fontSize: 13 }} />
                      }
                      sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700,
                        bgcolor: alpha(oc.color, 0.1), color: oc.color,
                        '& .MuiChip-icon': { color: oc.color },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={tdSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: pnlClr }}>
                      {s.pnl_pct != null ? fmtPct(s.pnl_pct) : '–'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#64748b' }}>
                    {s.hold_days != null ? `${s.hold_days}d` : '–'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SignalHistoryPanel;
